export interface AuthState {
  status: 'anonymous' | 'authenticated';
  token: string | null;
  email: string | null;
  displayName: string | null;
  userId: number | null;
  roleName: string | null;
  permissions: string[];
  /** Absolute access-token expiry, epoch ms. */
  expiresAt: number | null;
}

export interface AuthSession {
  token: string;
  email: string;
  displayName: string | null;
  userId: number;
  roleName: string;
  permissions: string[];
  expiresAt: number;
}

export type AuthAction =
  | { type: 'login'; session: AuthSession }
  | { type: 'rotateTokens'; token: string; expiresAt: number }
  | { type: 'logout' };

export const anonymousState: AuthState = {
  status: 'anonymous',
  token: null,
  email: null,
  displayName: null,
  userId: null,
  roleName: null,
  permissions: [],
  expiresAt: null,
};

export function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'login':
      return {
        status: 'authenticated',
        token: action.session.token,
        email: action.session.email,
        displayName: action.session.displayName,
        userId: action.session.userId,
        roleName: action.session.roleName,
        permissions: action.session.permissions,
        expiresAt: action.session.expiresAt,
      };
    case 'rotateTokens':
      // A refresh rotates the token pair only -- identity/permissions are NOT
      // re-fetched on every rotation (E2 brief), so every other field is
      // carried over unchanged.
      if (state.status !== 'authenticated') return state;
      return {
        ...state,
        token: action.token,
        expiresAt: action.expiresAt,
      };
    case 'logout':
      return anonymousState;
    default:
      return state;
  }
}

/** True once the session's expiry has passed (or there is no session). */
export function isExpired(state: AuthState, nowMs: number): boolean {
  if (state.status !== 'authenticated' || state.expiresAt === null) return true;
  return nowMs >= state.expiresAt;
}

/** True when `state` carries the given permission code. */
export function hasPermission(state: AuthState, code: string): boolean {
  return state.status === 'authenticated' && state.permissions.includes(code);
}
